import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { gsap } from 'gsap';
import { TextPlugin } from 'gsap/TextPlugin';

// Register GSAP plugins
gsap.registerPlugin(TextPlugin);

const Hero: React.FC = () => {
  const headingRef = useRef<HTMLHeadingElement>(null);
  const subheadingRef = useRef<HTMLParagraphElement>(null);
  const ctaRef = useRef<HTMLButtonElement>(null);
  const backgroundRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Create a timeline for coordinated animations
    const tl = gsap.timeline();

    // Initial setup - hide elements
    gsap.set([headingRef.current, subheadingRef.current, ctaRef.current], {
      opacity: 0,
      y: 50
    });

    // Background parallax effect
    gsap.set(backgroundRef.current, {
      scale: 1.1
    });

    // Animate background
    tl.to(backgroundRef.current, {
      scale: 1,
      duration: 2,
      ease: "power2.out"
    }, 0);

    // Staggered text animations
    tl.to(headingRef.current, {
      opacity: 1,
      y: 0,
      duration: 1,
      ease: "power2.out"
    }, 0.5);

    tl.to(subheadingRef.current, {
      opacity: 1,
      y: 0,
      duration: 1,
      ease: "power2.out"
    }, 0.8);

    tl.to(ctaRef.current, {
      opacity: 1,
      y: 0,
      duration: 1,
      ease: "power2.out"
    }, 1.1);

    // Add floating animation to CTA button
    gsap.to(ctaRef.current, {
      y: -5,
      duration: 2,
      ease: "power1.inOut",
      yoyo: true,
      repeat: -1,
      delay: 2
    });

    // Add subtle background movement
    gsap.to(backgroundRef.current, {
      y: -20,
      duration: 20,
      ease: "none",
      repeat: -1,
      yoyo: true
    });

  }, []);

  const handleExploreVehicles = () => {
    // Add click animation
    gsap.to(ctaRef.current, {
      scale: 0.95,
      duration: 0.1,
      yoyo: true,
      repeat: 1,
      onComplete: () => {
        navigate('/passenger-vehicles');
      }
    });
  };

  return (
    <section id="home" className="relative h-screen flex items-center overflow-hidden">
      {/* Background image with overlay */}
      <div ref={backgroundRef} className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/30 z-10"></div>
        <img
          src="/l5 loader render.458.avif"
          alt="Dawki Mobility electric three-wheeler"
          className="w-full h-full object-cover"
        />
      </div>

      {/* Content */}
      <div ref={containerRef} className="container mx-auto px-4 sm:px-6 lg:px-8 z-10 relative h-full flex flex-col justify-between">
        {/* Top section - Heading */}
        <div className="text-center pt-20">
          <h1
            ref={headingRef}
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-white pt-8"
          >
            Revolutionizing Last <br /> Mile Mobility
          </h1>
        </div>

        {/* Bottom section - Text and Button */}
        <div className="text-center pb-20">
          <div className="max-w-2xl mx-auto flex flex-col items-center">
            <p
              ref={subheadingRef}
              className="text-xl md:text-2xl text-[#F5F5F5] mb-4"
            >
              India's most sustainable, efficient & reliable EV three-wheelers for L5 Cargo & Passenger vehicles.
            </p>
            <button
              ref={ctaRef}
              onClick={handleExploreVehicles}
              className="bg-[#CC0000] hover:bg-[#AA0000] text-white font-medium py-3 px-8 rounded-md transition-all duration-300 flex items-center cursor-pointer hover:shadow-lg transform hover:scale-105"
              title="Click to explore our passenger vehicles"
            >
              Explore Our Vehicles
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;